import React, { Component, createRef } from 'react';
import axios from '../../utils/axiosInstance';
import 'bootstrap/dist/css/bootstrap.min.css';
import './PostDetail.css';
import Facilities from './Facilities';
import GuestReviews from './GuestReviews';
import PostFAQ from './PostFAQ';
import { withNavigation } from '../../utils/withNavigation';

const CANCELLATION_POLICY = [
  '80% of the total booking amount will be refunded if it is cancelled 24 hours before check-in.',
  'No refund is given for cancellations made less than 24 hours before check-in.',
  'No refund is given for no-shows or early departures.',
];

const REPORT_REASONS = [
  "It's inaccurate or incorrect",
  "It's not a real place",
  "It's a scam",
  "It's offensive",
];

const GUEST_OPTIONS = [
  { type: 'rooms', label: 'Rooms', hint: 'Number of rooms' },
  { type: 'adults', label: 'Adults', hint: 'Ages 13 or above' },
  { type: 'children', label: 'Children', hint: 'Ages 2–12' },
];

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const DAY_MS = 24 * 60 * 60 * 1000;
const DESCRIPTION_CLAMP_LENGTH = 900;
const RECENTLY_VIEWED_KEY = 'recently_viewed_products';
const RECENTLY_VIEWED_LIMIT = 20;
const BRAND_HEX = '0049b0'; // keep in sync with --bookly-primary in PostDetail.css

/* ---------- Pure helpers ---------- */

const stripHtml = (html) => String(html || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

const formatMoney = (value) => Number(value || 0).toLocaleString('en-BD', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

// Optional numeric fields are sent as '' when empty, matching the existing API contract.
const numberOrBlank = (value) => (value ? Number(value) : '');

const pick = (...values) => values.find(v => v !== undefined && v !== null && v !== '');

const getLocalDateString = (date) => {
  if (!date) return '';
  const d = new Date(date);
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
};

const formatDateLabel = (date) => (
  date ? date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Select Date'
);

const countNights = (checkIn, checkOut) => (
  checkIn && checkOut ? Math.ceil(Math.abs(checkOut - checkIn) / DAY_MS) : 0
);

const buildGallery = (data) => {
  let gallery = [];
  try {
    const parsed = data.gallery_images ? JSON.parse(data.gallery_images) : [];
    gallery = Array.isArray(parsed) ? parsed.filter(Boolean) : [];
  } catch {
    gallery = [];
  }
  return data.feature_image
    ? [data.feature_image, ...gallery.filter(image => image !== data.feature_image)]
    : gallery;
};

// Uses an explicit tenure field when the API provides one, otherwise derives it from the join date.
const getHostTenure = (hotel) => {
  const value = pick(hotel.host_tenure_value, hotel.tenure_value);
  const label = pick(hotel.host_tenure_label, hotel.tenure_label);
  if (value) return { value, label: label || 'Hosting on Bookly' };

  if (!hotel.customer_date) return { value: '—', label: 'Hosting on Bookly' };
  const start = new Date(hotel.customer_date);
  if (Number.isNaN(start.getTime())) return { value: '—', label: 'Hosting on Bookly' };

  const days = Math.max(0, Math.floor((Date.now() - start) / DAY_MS));
  const years = Math.floor(days / 365);
  const months = Math.floor((days % 365) / 30);
  if (years > 0) return { value: String(years), label: years === 1 ? 'year hosting' : 'years hosting' };
  if (months > 0) return { value: String(months), label: months === 1 ? 'month hosting' : 'months hosting' };
  return { value: 'New', label: 'Hosting on Bookly' };
};

const getHostInfo = (hotel, imageBase) => {
  const name = pick(hotel.host_name, hotel.full_name) || 'Host';
  const rating = Number(pick(hotel.host_rating, hotel.host_ratings, hotel.customer_rating));
  const reviewCount = Number(pick(hotel.host_reviews, hotel.host_total_reviews, hotel.customer_reviews)) || 0;

  return {
    name,
    profileUrl: `/host-profile/${hotel.customer_id || ''}`,
    avatar: hotel.host_image
      ? `${imageBase}${hotel.host_image}`
      : `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=${BRAND_HEX}&color=fff`,
    rating: Number.isFinite(rating) && rating > 0 ? rating.toFixed(2) : 'New',
    reviews: reviewCount > 0 ? `${reviewCount} Reviews` : 'No reviews yet',
    tenure: getHostTenure(hotel),
  };
};

class PostDetail extends Component {
  constructor(props) {
    super(props);

    const today = new Date();
    const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    this.guestRef = createRef();
    this.calendarRef = createRef();
    this.copyTimer = null;

    this.state = {
      hotel: null,
      loading: true,
      error: null,
      bookedDates: [],
      social: { email: '', whatsapp: '', facebook: '', twitter: '' },

      checkIn: null,
      checkOut: null,
      calendarBase: firstOfMonth,
      sidebarCalendarBase: firstOfMonth,
      showSidebarCalendar: false,

      rooms: 1,
      adults: 1,
      children: 0,
      isGuestOpen: false,
      remarks: '',
      post_booking_status: 0,

      isGalleryOpen: false,
      currentImageIndex: 0,
      isFavorite: false,
      copied: false,

      activeModal: null, // 'share' | 'details' | 'cancellation' | 'rules' | 'report'
      reportForm: { title: '', description: '' },
      reportSubmitting: false,
      reportError: '',
      reportSubmitted: false,
    };
  }

  componentDidMount() {
    document.addEventListener('keydown', this.handleKeyDown);
    document.addEventListener('mousedown', this.handleOutsideClick);
    this.fetchSocialLinks();
    this.loadListing(this.props.params?.id);
  }

  componentDidUpdate(prevProps) {
    const id = this.props.params?.id;
    if (id !== prevProps.params?.id) {
      window.scrollTo({ top: 0 });
      this.loadListing(id);
    }
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown);
    document.removeEventListener('mousedown', this.handleOutsideClick);
    clearTimeout(this.copyTimer);
    document.body.style.overflow = 'unset';
  }

  /* ---------- Data loading ---------- */

  loadListing = (id) => {
    if (!id) return;
    this.setState({ loading: true, error: null, checkIn: null, checkOut: null });
    this.loadPost(id);
    this.fetchBookedDates(id);
    this.updateRecentlyViewed(id);
  };

  loadPost = async (postId) => {
    try {
      const res = await axios.get(`/get_post_by_id?id=${postId}`);
      const data = Array.isArray(res.data?.data) ? res.data.data[0] : res.data?.data;

      if (!data) {
        this.setState({ error: 'Property not found', loading: false });
        return;
      }

      this.setState({
        hotel: { ...data, all_images: buildGallery(data) },
        post_booking_status: Number(data.post_booking_status) || 0,
        adults: data.adults ? Math.min(2, data.adults) : 1,
        loading: false,
      });
    } catch (err) {
      console.error('Fetch error:', err);
      this.setState({ error: 'Failed to load property details', loading: false });
    }
  };

  fetchSocialLinks = async () => {
    try {
      const res = await axios.get('/theme_customizable');
      const info = res?.data?.data;
      if (!info) return;
      this.setState({
        social: {
          email: info.email || '',
          whatsapp: info.whatsapp_number || '',
          facebook: info.social_link_1 || '',
          twitter: info.social_link_2 || '',
        },
      });
    } catch (error) {
      console.error('Share links fetch error:', error);
    }
  };

  fetchBookedDates = async (postId) => {
    try {
      const res = await axios.get(`/booked_dates?post_id=${postId}`);
      this.setState({ bookedDates: res.data?.data || [] });
    } catch (e) {
      console.error('Booked dates fetch error:', e);
    }
  };

  updateRecentlyViewed = (id) => {
    try {
      const stored = JSON.parse(localStorage.getItem(RECENTLY_VIEWED_KEY) || '[]');
      const list = Array.isArray(stored) ? stored : [];
      const next = [id, ...list.filter(item => Number(item) !== Number(id))];
      localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify(next.slice(0, RECENTLY_VIEWED_LIMIT)));
    } catch {
      // Recently viewed is optional; a storage problem must not affect the page.
    }
  };

  /* ---------- Booking ---------- */

  getCustomerId = () => {
    const customerId = localStorage.getItem('web_user_id');
    if (!customerId) alert('Please login first');
    return customerId;
  };

  handleBooking = async () => {
    const { hotel, checkIn, checkOut, rooms, adults, children, remarks } = this.state;
    const customerId = this.getCustomerId();
    if (!customerId) return;

    if (!checkIn || !checkOut) {
      alert('Select dates first');
      return;
    }
    if (checkIn >= checkOut) {
      alert('Check-out date must be after check-in date.');
      return;
    }
    if (!this.isDateRangeFree(checkIn, checkOut)) {
      alert('Your selected range contains already booked dates. Please select free dates.');
      return;
    }

    const { total } = this.calculateTotals();
    try {
      await axios.post('book_room', {
        post_id: Number(hotel.id),
        customer_id: Number(customerId),
        host_id: Number(hotel.customer_id),
        checkin_date: getLocalDateString(checkIn),
        checkout_date: getLocalDateString(checkOut),
        rooms: Number(rooms),
        adults: Number(adults),
        children: Number(children),
        remarks,
        totalAmount: Number(total),
      });
      window.location.href = '/chat';
    } catch (e) {
      console.error(e);
      alert('Booking failed');
    }
  };

  handleBookingDirect = async () => {
    const { hotel, rooms, adults, children, remarks } = this.state;
    const customerId = this.getCustomerId();
    if (!customerId) return;

    const { total } = this.calculateTotals();
    const today = getLocalDateString(new Date());
    try {
      await axios.post('book_room_direct', {
        post_id: Number(hotel.id),
        customer_id: Number(customerId),
        host_id: Number(hotel.customer_id),
        checkin_date: today,
        checkout_date: today,
        rooms: numberOrBlank(rooms),
        adults: numberOrBlank(adults),
        children: numberOrBlank(children),
        remarks: remarks || '',
        totalAmount: numberOrBlank(total),
      });
      window.location.href = '/chat';
    } catch (e) {
      console.error(e);
      alert('Booking failed');
    }
  };

  handleMobileAction = () => {
    const { checkIn, checkOut } = this.state;
    if (checkIn && checkOut) {
      this.handleBooking();
      return;
    }
    this.calendarRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  calculateTotals = () => {
    const { hotel, rooms, checkIn, checkOut } = this.state;
    const pricePerNight = Number(hotel?.sales_price) || 0;
    const totalNights = countNights(checkIn, checkOut) || 1;
    const total = pricePerNight * totalNights * Number(rooms || 1);
    return { pricePerNight, totalNights, total };
  };

  isDateBlockedForCheckIn = (date) => {
    const dateStr = getLocalDateString(date);
    return this.state.bookedDates.some(b => dateStr >= b.checkin_date && dateStr < b.checkout_date);
  };

  isDateRangeFree = (start, end) => {
    const startStr = getLocalDateString(start);
    const endStr = getLocalDateString(end);
    return !this.state.bookedDates.some(b => startStr < b.checkout_date && endStr > b.checkin_date);
  };

  /* ---------- Share, gallery, favourite ---------- */

  copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      this.setState({ copied: true });
      clearTimeout(this.copyTimer);
      this.copyTimer = setTimeout(() => this.setState({ copied: false }), 3000);
    } catch (e) {
      console.warn('Copy failed', e);
    }
  };

  openGallery = (index = 0) => {
    this.setState({ isGalleryOpen: true, currentImageIndex: index });
    document.body.style.overflow = 'hidden';
  };

  closeGallery = () => {
    this.setState({ isGalleryOpen: false });
    document.body.style.overflow = 'unset';
  };

  stepImage = (step) => this.setState((p) => {
    const total = p.hotel?.all_images?.length || 1;
    return { currentImageIndex: (p.currentImageIndex + step + total) % total };
  });

  nextImage = () => this.stepImage(1);

  prevImage = () => this.stepImage(-1);

  toggleFavorite = () => this.setState(p => ({ isFavorite: !p.isFavorite }));

  goBack = () => (this.props.navigate ? this.props.navigate(-1) : window.history.back());

  /* ---------- Dialogs ---------- */

  openModal = (name) => {
    this.setState({ activeModal: name });
    document.body.style.overflow = 'hidden';
  };

  closeModal = () => {
    this.setState({
      activeModal: null,
      reportForm: { title: '', description: '' },
      reportSubmitting: false,
      reportError: '',
      reportSubmitted: false,
    });
    document.body.style.overflow = 'unset';
  };

  handleReportChange = (e) => {
    const { name, value } = e.target;
    this.setState(p => ({ reportForm: { ...p.reportForm, [name]: value }, reportError: '' }));
  };

  handleReportSubmit = async (e) => {
    e?.preventDefault();
    const { hotel, reportForm } = this.state;
    const customerId = localStorage.getItem('web_user_id');
    const title = reportForm.title.trim();
    const description = reportForm.description.trim();

    if (!customerId) {
      this.setState({ reportError: 'Please login first to report this listing.' });
      return;
    }
    if (!title || !description) {
      this.setState({ reportError: 'Please enter a title and a description.' });
      return;
    }

    this.setState({ reportSubmitting: true, reportError: '' });
    try {
      await axios.post('report_listing', {
        title,
        description,
        post_id: Number(hotel.id),
        customer_id: Number(customerId),
      });
      this.setState({
        reportSubmitted: true,
        reportSubmitting: false,
        reportForm: { title: '', description: '' },
      });
    } catch (err) {
      console.error('Report error:', err);
      const message = err.response?.data?.messages?.error
        || err.response?.data?.message
        || 'Failed to submit your report. Please try again.';
      this.setState({ reportError: message, reportSubmitting: false });
    }
  };

  handleKeyDown = (e) => {
    const { isGalleryOpen, activeModal, isGuestOpen, showSidebarCalendar } = this.state;

    if (isGalleryOpen) {
      if (e.key === 'ArrowRight') this.nextImage();
      if (e.key === 'ArrowLeft') this.prevImage();
      if (e.key === 'Escape') this.closeGallery();
      return;
    }

    if (e.key !== 'Escape') return;
    if (activeModal) this.closeModal();
    else if (isGuestOpen) this.setState({ isGuestOpen: false });
    else if (showSidebarCalendar) this.setState({ showSidebarCalendar: false });
  };

  handleOutsideClick = (e) => {
    if (this.state.isGuestOpen && this.guestRef.current && !this.guestRef.current.contains(e.target)) {
      this.setState({ isGuestOpen: false });
    }
  };

  /* ---------- Calendar ---------- */

  handleDayClick = (clicked) => {
    const { checkIn, checkOut } = this.state;
    const todayStr = getLocalDateString(new Date());
    if (getLocalDateString(clicked) < todayStr) return;

    if (!checkIn || checkOut) {
      if (!this.isDateBlockedForCheckIn(clicked)) this.setState({ checkIn: clicked, checkOut: null });
    } else if (clicked <= checkIn) {
      if (!this.isDateBlockedForCheckIn(clicked)) this.setState({ checkIn: clicked });
    } else if (this.isDateRangeFree(checkIn, clicked)) {
      this.setState({ checkOut: clicked, showSidebarCalendar: false });
    } else if (!this.isDateBlockedForCheckIn(clicked)) {
      this.setState({ checkIn: clicked });
    }
  };

  clearDates = () => this.setState({ checkIn: null, checkOut: null });

  changeMonth = (key, offset) => this.setState(p => ({
    [key]: new Date(p[key].getFullYear(), p[key].getMonth() + offset, 1),
  }));

  renderCalendarMonth = (baseDate) => {
    const { checkIn, checkOut } = this.state;
    const year = baseDate.getFullYear();
    const month = baseDate.getMonth();
    const firstWeekday = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const todayStr = getLocalDateString(new Date());
    const cells = [];

    for (let i = 0; i < firstWeekday; i++) {
      cells.push(<div key={`empty-${i}`} className="cal-day empty" />);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const isPast = getLocalDateString(date) < todayStr;

      let isBooked;
      if (isPast) isBooked = true;
      else if (!checkIn || checkOut || date <= checkIn) isBooked = this.isDateBlockedForCheckIn(date);
      else isBooked = !this.isDateRangeFree(checkIn, date);

      const classes = ['cal-day'];
      if (isBooked) classes.push('is-unavailable');
      if (checkIn && date.getTime() === checkIn.getTime()) classes.push('selected');
      else if (checkOut && date.getTime() === checkOut.getTime()) classes.push('selected');
      else if (checkIn && checkOut && date > checkIn && date < checkOut) classes.push('in-range');

      cells.push(
        <button
          type="button"
          key={day}
          className={classes.join(' ')}
          onClick={() => this.handleDayClick(date)}
          disabled={isBooked}
          aria-label={`Select ${date.toDateString()}`}
        >
          {day}
        </button>,
      );
    }

    return (
      <div className="calendar-month-wrapper">
        <div className="calendar-month-title">
          {baseDate.toLocaleString('en-US', { month: 'long', year: 'numeric' })}
        </div>
        <div className="cal-weekdays">
          {WEEKDAYS.map(day => <div key={day}>{day}</div>)}
        </div>
        <div className="cal-grid">{cells}</div>
      </div>
    );
  };

  /* ---------- Guests ---------- */

  getGuestLimits = (type) => {
    const { hotel } = this.state;
    if (type === 'rooms') return { min: 1, max: Number(hotel?.room_number) || 10 };
    if (type === 'adults') return { min: 1, max: Number(hotel?.adults) || 10 };
    return { min: 0, max: Number(hotel?.children) || 10 };
  };

  handleGuestChange = (type, delta) => {
    this.setState((p) => {
      const { min, max } = this.getGuestLimits(type);
      return { [type]: Math.min(max, Math.max(min, p[type] + delta)) };
    });
  };

  getGuestSummary = () => {
    const { rooms, adults, children } = this.state;
    const guests = adults + children;
    const parts = [`${guests} ${guests === 1 ? 'guest' : 'guests'}`];
    if (rooms > 1) parts.push(`${rooms} rooms`);
    return parts.join(', ');
  };

  toggleGuestDropdown = () => this.setState(p => ({
    isGuestOpen: !p.isGuestOpen,
    showSidebarCalendar: false,
  }));

  toggleSidebarCalendar = () => this.setState(p => ({
    showSidebarCalendar: !p.showSidebarCalendar,
    isGuestOpen: false,
  }));

  closeSidebarCalendar = () => this.setState({ showSidebarCalendar: false });

  handleRemarksChange = (e) => this.setState({ remarks: e.target.value });

  /* ---------- Media URLs ---------- */

  getUploadUrl = () => {
    const base = axios.defaults.baseURL ? axios.defaults.baseURL.replace(/\/api\/?$/, '') : '';
    return `${base}/uploads/posts/`;
  };

  getImageUrls = () => {
    const base = this.getUploadUrl();
    const images = this.state.hotel?.all_images || [];
    return images.filter(Boolean).map(image => `${base}${image}`);
  };

  /* ---------- Modal content ---------- */

  renderModal = (title, body, { wide = false } = {}) => (
    <div className="custom-modal-overlay" onClick={this.closeModal}>
      <div
        className={`bk-modal animate-zoom${wide ? ' bk-modal-wide' : ''}`}
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="bk-modal-header">
          <h4 className="bk-modal-title">{title}</h4>
          <button type="button" className="modal-close-btn" onClick={this.closeModal} aria-label="Close">
            <i className="fas fa-times" />
          </button>
        </div>
        <div className="bk-modal-body">{body}</div>
      </div>
    </div>
  );

  renderShareBody = (images) => {
    const { hotel, social, copied } = this.state;
    return (
      <>
        <div className="share-property-row">
          {images[0] && <img src={images[0]} alt={hotel.title} className="share-property-image" />}
          <div className="share-property-copy">
            <h6>{hotel.title}</h6>
            <p>{hotel.category_name || 'Property listing'}</p>
          </div>
        </div>

        <div className="share-options-grid">
          <a href={social.facebook || '#'} target="_blank" rel="noopener noreferrer" className="share-option">
            <i className="fab fa-facebook" /><span>Facebook</span>
          </a>
          <a href={`https://wa.me/${social.whatsapp}`} target="_blank" rel="noopener noreferrer" className="share-option">
            <i className="fab fa-whatsapp" /><span>WhatsApp</span>
          </a>
          <a href={social.twitter || '#'} target="_blank" rel="noopener noreferrer" className="share-option">
            <i className="fab fa-twitter" /><span>Twitter</span>
          </a>
          <a href={`mailto:${social.email}`} className="share-option">
            <i className="fas fa-envelope" /><span>Email</span>
          </a>
        </div>

        <div className="share-copy-row">
          <span>{window.location.href}</span>
          <button type="button" onClick={this.copyLink}>{copied ? 'Copied!' : 'Copy'}</button>
        </div>
      </>
    );
  };

  renderReportSuccess = () => (
    <div className="report-success">
      <i className="fas fa-check-circle" />
      <h5>Thanks for your report</h5>
      <p>Our team will review this listing.</p>
      <button type="button" className="bk-primary-btn" onClick={this.closeModal}>Done</button>
    </div>
  );

  renderReportForm = () => {
    const { reportForm, reportSubmitting, reportError } = this.state;
    return (
      <form className="report-form" onSubmit={this.handleReportSubmit}>
        <div className="report-field">
          <label htmlFor="report-title">Title</label>
          <input
            id="report-title"
            className="report-input"
            type="text"
            name="title"
            maxLength={100}
            placeholder="What is the problem?"
            value={reportForm.title}
            onChange={this.handleReportChange}
          />
          <div className="report-chips">
            {REPORT_REASONS.map(reason => (
              <button
                type="button"
                key={reason}
                className={`report-chip ${reportForm.title === reason ? 'is-selected' : ''}`}
                onClick={() => this.handleReportChange({ target: { name: 'title', value: reason } })}
              >
                {reason}
              </button>
            ))}
          </div>
        </div>

        <div className="report-field">
          <label htmlFor="report-description">Description</label>
          <textarea
            id="report-description"
            className="report-textarea"
            rows={5}
            name="description"
            maxLength={1000}
            placeholder="Tell us what's wrong"
            value={reportForm.description}
            onChange={this.handleReportChange}
          />
        </div>

        {reportError && <div className="report-error" role="alert">{reportError}</div>}

        <button type="submit" className="bk-primary-btn" disabled={reportSubmitting}>
          {reportSubmitting ? 'Submitting...' : 'Submit'}
        </button>
      </form>
    );
  };

  renderActiveModal = (images) => {
    const { activeModal, hotel } = this.state;

    switch (activeModal) {
      case 'share':
        return this.renderModal('Share this listing', this.renderShareBody(images));
      case 'details':
        return this.renderModal(
          'About this place',
          <div className="section-text" dangerouslySetInnerHTML={{ __html: hotel.content || '' }} />,
          { wide: true },
        );
      case 'cancellation':
        return this.renderModal(
          'Cancellation policy',
          <ul className="policy-list">
            {CANCELLATION_POLICY.map(line => <li key={line}>{line}</li>)}
          </ul>,
        );
      case 'rules':
        return this.renderModal(
          'House rules',
          <ul className="policy-list">
            <li>Check-in after {hotel.checkin_time || '—'}</li>
            <li>Checkout before {hotel.checkout_time || '—'}</li>
            <li>{hotel.adults || 2} guests maximum</li>
          </ul>,
        );
      case 'report':
        return this.renderModal(
          'Report this listing',
          this.state.reportSubmitted ? this.renderReportSuccess() : this.renderReportForm(),
        );
      default:
        return null;
    }
  };

  renderFullscreenGallery = (images, currentIndex) => (
    <div className="fullscreen-gallery" role="dialog" aria-modal="true" aria-label="Property photo gallery">
      <button type="button" className="gallery-close" onClick={this.closeGallery} aria-label="Close gallery">
        <i className="fas fa-times" />
      </button>
      <div className="gallery-counter">{currentIndex + 1} / {images.length}</div>
      <button type="button" className="gallery-nav prev" onClick={this.prevImage} aria-label="Previous image">
        <i className="fas fa-chevron-left" />
      </button>
      <img src={images[currentIndex]} alt={`Property view ${currentIndex + 1}`} className="gallery-main-img" />
      <button type="button" className="gallery-nav next" onClick={this.nextImage} aria-label="Next image">
        <i className="fas fa-chevron-right" />
      </button>
      <div className="gallery-thumbnails">
        {images.map((image, index) => (
          <button
            type="button"
            key={`${image}-${index}`}
            className={`gallery-thumb-button ${currentIndex === index ? 'active' : ''}`}
            onClick={() => this.setState({ currentImageIndex: index })}
            aria-label={`Show photo ${index + 1}`}
            aria-pressed={currentIndex === index}
          >
            <img src={image} alt="" className="gallery-thumb" loading="lazy" />
          </button>
        ))}
      </div>
    </div>
  );

  /* ---------- Render ---------- */

  render() {
    const {
      hotel, loading, error, checkIn, checkOut, rooms, isGuestOpen, showSidebarCalendar,
      calendarBase, sidebarCalendarBase, isGalleryOpen, currentImageIndex, remarks,
      isFavorite, post_booking_status,
    } = this.state;

    if (loading) {
      return (
        <div className="property-details-wrapper">
          <div className="post-detail-state container" role="status" aria-live="polite">
            <span className="post-detail-spinner" />
            <p>Loading property details...</p>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="property-details-wrapper">
          <div className="post-detail-state container">
            <div className="post-detail-error" role="alert">{error}</div>
          </div>
        </div>
      );
    }

    if (!hotel) return null;

    const images = this.getImageUrls();
    const host = getHostInfo(hotel, this.getUploadUrl());
    const { pricePerNight, totalNights, total } = this.calculateTotals();
    const listingId = this.props.params?.id;
    const hasDates = Boolean(checkIn && checkOut);
    const isInstant = post_booking_status === 1;
    const hasLongDetails = stripHtml(hotel.content).length > DESCRIPTION_CLAMP_LENGTH;
    const formattedPrice = Number(pricePerNight).toLocaleString('en-BD');
    const nextMonthBase = new Date(calendarBase.getFullYear(), calendarBase.getMonth() + 1, 1);

    return (
      <div className="property-details-wrapper">
        {isGalleryOpen && images.length > 0 && this.renderFullscreenGallery(images, currentImageIndex)}
        {this.renderActiveModal(images)}

        <div className="container property-container">
          <header className="property-header animate-slide-up">
            <div className="property-header-copy">
              <h1 className="property-title">{hotel.title}</h1>
              <div className="property-header-tags">
                {hotel.category_name && <span className="property-type-badge">{hotel.category_name}</span>}
                {hotel.room_type && <span className="property-type-badge is-alt">{hotel.room_type}</span>}
                <span className="property-rating-chip" title="Listing rating">
                  <i className="fas fa-star" /> {hotel.ratings || 'New'}
                </span>
              </div>
            </div>

            <div className="property-header-actions">
              <button type="button" className="circle-action" onClick={() => this.openModal('share')} aria-label="Share property">
                <i className="fas fa-share-alt" />
              </button>
              <button
                type="button"
                className={`circle-action favorite-action ${isFavorite ? 'is-favorite' : ''}`}
                onClick={this.toggleFavorite}
                aria-label={isFavorite ? 'Remove from saved properties' : 'Save property'}
                aria-pressed={isFavorite}
              >
                <i className={isFavorite ? 'fas fa-heart' : 'far fa-heart'} />
              </button>
            </div>
          </header>

          <div className={`property-grid-gallery animate-fade-in${images.length > 1 ? '' : ' is-single'}`}>
            <div className="mobile-gallery-top-bar">
              <button type="button" className="mobile-round-btn" onClick={this.goBack} aria-label="Go back">
                <i className="fas fa-arrow-left" />
              </button>
              <div className="mobile-gallery-actions">
                <button type="button" className="mobile-share-btn" onClick={() => this.openModal('share')} aria-label="Share listing">
                  <i className="fas fa-share-alt" /><span>Share</span>
                </button>
                <button
                  type="button"
                  className={`mobile-round-btn ${isFavorite ? 'is-favorite' : ''}`}
                  onClick={this.toggleFavorite}
                  aria-label={isFavorite ? 'Remove from saved properties' : 'Save listing'}
                  aria-pressed={isFavorite}
                >
                  <i className={isFavorite ? 'fas fa-heart' : 'far fa-heart'} />
                </button>
              </div>
            </div>

            <button
              type="button"
              className="gallery-main"
              onClick={() => this.openGallery(0)}
              aria-label="Open main property image"
              disabled={!images.length}
            >
              {images[0]
                ? <img src={images[0]} alt="Main property view" fetchPriority="high" decoding="async" />
                : <span className="gallery-empty">No property photos available</span>}
            </button>

            {images.length > 1 && (
              <div className="gallery-side">
                {images.slice(1, 5).map((image, index) => (
                  <button
                    type="button"
                    key={`${image}-${index}`}
                    className="gallery-side-item"
                    onClick={() => this.openGallery(index + 1)}
                    aria-label={`Open property photo ${index + 2}`}
                  >
                    <img src={image} alt={`Property view ${index + 2}`} loading="lazy" decoding="async" />
                    {index === 3 && images.length > 5 && (
                      <span className="more-images-overlay">+{images.length - 5} more</span>
                    )}
                  </button>
                ))}
              </div>
            )}

            {images.length > 1 && (
              <button type="button" className="show-all-photos-btn" onClick={() => this.openGallery(0)}>
                <i className="fas fa-th" /> Show all photos
              </button>
            )}
          </div>

          <div className="detail-badges">
            <span className="detail-badge"><i className="fas fa-user-friends" /> {hotel.adults || 0} Guests</span>
            <span className="detail-badge"><i className="fas fa-bed" /> {hotel.bedrooms || hotel.rooms || 1} Bedrooms</span>
            <span className="detail-badge"><i className="fas fa-bed" /> {hotel.beds || 0} Beds</span>
            <span className="detail-badge"><i className="fas fa-bath" /> {hotel.baths || 0} Baths</span>
          </div>

          <div className="row g-5">
            <main className="col-lg-8 property-main-content animate-slide-up">
              <section className="detail-section">
                <h2 className="section-heading">Details</h2>
                <div
                  className={`section-text ${hasLongDetails ? 'is-clamped' : ''}`}
                  dangerouslySetInnerHTML={{ __html: hotel.content || '' }}
                />
                {hasLongDetails && (
                  <button type="button" className="show-more-btn" onClick={() => this.openModal('details')}>
                    Show more <i className="fas fa-chevron-right" />
                  </button>
                )}
              </section>

              <section className="detail-section" ref={this.calendarRef}>
                <h2 className="section-heading">Select your dates</h2>
                <p className="section-subtext">
                  {hasDates
                    ? <span className="selected-date-text">Selected: {formatDateLabel(checkIn)} – {formatDateLabel(checkOut)} ({countNights(checkIn, checkOut)} nights)</span>
                    : 'Minimum stay: 1 night'}
                </p>

                <div className="calendar-box">
                  <button type="button" className="cal-nav-btn cal-nav-left" onClick={() => this.changeMonth('calendarBase', -1)} aria-label="Previous month">
                    <i className="fas fa-chevron-left" />
                  </button>
                  <button type="button" className="cal-nav-btn cal-nav-right" onClick={() => this.changeMonth('calendarBase', 1)} aria-label="Next month">
                    <i className="fas fa-chevron-right" />
                  </button>
                  <div className="calendar-months">
                    <div className="calendar-column">{this.renderCalendarMonth(calendarBase)}</div>
                    <div className="calendar-column calendar-second">{this.renderCalendarMonth(nextMonthBase)}</div>
                  </div>
                  <div className="calendar-footer">
                    <button type="button" className="clear-date-btn" onClick={this.clearDates}>Clear dates</button>
                  </div>
                </div>
              </section>

              <Facilities postId={listingId} />

              {Boolean(hotel.map_latitude && hotel.map_longitude) && (
                <section className="detail-section">
                  <h2 className="section-heading">Map</h2>
                  <div className="map-card">
                    <iframe
                      className="map-frame"
                      title="Property location map"
                      src={`https://www.google.com/maps?ll=${hotel.map_latitude},${hotel.map_longitude}&z=14&output=embed`}
                      loading="lazy"
                      allowFullScreen
                    />
                    <div className="map-pin" aria-hidden="true"><i className="fas fa-home" /></div>
                  </div>
                </section>
              )}

              <section className="detail-section">
                <h2 className="section-heading">Meet your host</h2>
                <div className="mh-layout">
                  <div className="mh-card">
                    <div className="mh-profile-col">
                      <div className="mh-avatar-box">
                        <img src={host.avatar} alt={host.name} loading="lazy" decoding="async" />
                      </div>
                      <span className="mh-verified-badge"><i className="fas fa-shield-alt" /> Verified</span>
                      <h3 className="mh-name">
                        <a href={host.profileUrl}>{host.name}</a>
                      </h3>
                    </div>
                    <div className="mh-stats-col">
                      <div className="mh-stat-item">
                        <div className="mh-rating-num"><i className="fas fa-star" /> {host.rating}</div>
                        <div className="mh-subtext">{host.reviews}</div>
                      </div>
                      <div className="mh-stat-item">
                        <div className="mh-big-num">{host.tenure.value}</div>
                        <div className="mh-subtext">{host.tenure.label}</div>
                      </div>
                    </div>
                  </div>

                  <div className="mh-side">
                    <div className="mh-info-row">
                      <div className="mh-info-icon-box"><i className="far fa-clock" /></div>
                      <div className="mh-info-text">
                        <span className="mh-info-label">Response time</span>
                        <span className="mh-info-val fast-resp">Within 14 minutes</span>
                      </div>
                    </div>
                    <div className="mh-info-row">
                      <div className="mh-info-icon-box lang-icon"><i className="fas fa-language" /></div>
                      <div className="mh-info-text">
                        <span className="mh-info-label">Languages</span>
                        <span className="mh-info-val">Bangla, English</span>
                      </div>
                    </div>
                    <button type="button" className="mh-message-host-btn" onClick={this.handleBookingDirect}>
                      Message host
                    </button>
                  </div>
                </div>
              </section>

              <section className="detail-section things-section">
                <h2 className="section-heading">Things to know</h2>
                <div className="things-grid">
                  <button type="button" className="thing-card" onClick={() => this.openModal('cancellation')}>
                    <span className="thing-head">
                      <i className="far fa-calendar-times" /><span>Cancellation policy</span>
                    </span>
                    <span className="thing-text thing-text-clamp">{CANCELLATION_POLICY[0]}</span>
                    <i className="fas fa-chevron-right thing-arrow" />
                  </button>
                  <button type="button" className="thing-card" onClick={() => this.openModal('rules')}>
                    <span className="thing-head"><i className="fas fa-key" /><span>House rules</span></span>
                    <span className="thing-text">
                      Check-in after {hotel.checkin_time || '—'}<br />
                      Checkout before {hotel.checkout_time || '—'}<br />
                      {hotel.adults || 2} guests maximum
                    </span>
                    <i className="fas fa-chevron-right thing-arrow" />
                  </button>
                </div>
              </section>

              <GuestReviews postId={listingId} />
              <PostFAQ postId={listingId} />
            </main>

            <aside className="col-lg-4 booking-column" id="booking-sidebar-section">
              <div className="booking-sidebar">
                <div className="booking-card">
                  <div className="booking-price-row">
                    <div className="booking-price-line">
                      <span className="booking-price">BDT {formattedPrice}</span>
                      <span className="booking-unit"> / day</span>
                    </div>
                    <div className={`booking-mode ${isInstant ? 'is-instant' : 'is-manual'}`}>
                      <i className={isInstant ? 'fas fa-bolt' : 'fas fa-clock'} />
                      <span>{isInstant ? 'Instant Book' : 'Manual Approval Required'}</span>
                    </div>
                  </div>

                  <div className="booking-box">
                    <button
                      type="button"
                      className="booking-date-row"
                      onClick={this.toggleSidebarCalendar}
                      aria-expanded={showSidebarCalendar}
                    >
                      <span className="booking-date-cell">
                        <i className="far fa-calendar-alt" />
                        <span className="booking-date-text">
                          <span className="booking-date-label">Check In</span>
                          <span className="booking-date-value">{formatDateLabel(checkIn)}</span>
                        </span>
                      </span>
                      <span className="booking-date-cell">
                        <i className="far fa-calendar-alt" />
                        <span className="booking-date-text">
                          <span className="booking-date-label">Check Out</span>
                          <span className="booking-date-value">{formatDateLabel(checkOut)}</span>
                        </span>
                      </span>
                    </button>

                    {showSidebarCalendar && (
                      <div className="sidebar-calendar-dropdown">
                        <div className="sidebar-calendar-header">
                          <button type="button" className="mini-nav-btn" onClick={() => this.changeMonth('sidebarCalendarBase', -1)} aria-label="Previous month">
                            <i className="fas fa-chevron-left" />
                          </button>
                          <span>Select dates</span>
                          <button type="button" className="mini-nav-btn" onClick={() => this.changeMonth('sidebarCalendarBase', 1)} aria-label="Next month">
                            <i className="fas fa-chevron-right" />
                          </button>
                        </div>
                        {this.renderCalendarMonth(sidebarCalendarBase)}
                        <div className="sidebar-calendar-footer">
                          <button type="button" className="sidebar-close-btn" onClick={this.closeSidebarCalendar}>Close</button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="guest-picker" ref={this.guestRef}>
                    <button
                      type="button"
                      className={`guest-trigger ${isGuestOpen ? 'is-open' : ''}`}
                      onClick={this.toggleGuestDropdown}
                      aria-expanded={isGuestOpen}
                    >
                      <i className="far fa-user" />
                      <span className="guest-trigger-text">
                        <span className="guest-trigger-label">Guests</span>
                        <span className="guest-trigger-value">{this.getGuestSummary()}</span>
                      </span>
                      <i className={`fas fa-chevron-${isGuestOpen ? 'up' : 'down'} guest-trigger-arrow`} />
                    </button>

                    {isGuestOpen && (
                      <div className="guest-dropdown animate-zoom">
                        {GUEST_OPTIONS.map(({ type, label, hint }) => {
                          const { min, max } = this.getGuestLimits(type);
                          const value = this.state[type];
                          return (
                            <div className="guest-option" key={type}>
                              <div>
                                <div className="guest-option-title">{label}</div>
                                <div className="guest-option-hint">{hint}</div>
                              </div>
                              <div className="guest-controls">
                                <button
                                  type="button"
                                  className="qty-btn"
                                  disabled={value <= min}
                                  onClick={() => this.handleGuestChange(type, -1)}
                                  aria-label={`Decrease ${label}`}
                                >
                                  <i className="fas fa-minus" />
                                </button>
                                <span className="guest-count">{value}</span>
                                <button
                                  type="button"
                                  className="qty-btn"
                                  disabled={value >= max}
                                  onClick={() => this.handleGuestChange(type, 1)}
                                  aria-label={`Increase ${label}`}
                                >
                                  <i className="fas fa-plus" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className="remarks-wrapper">
                    <div className="remarks-icon"><i className="far fa-comments" /></div>
                    <div className="remarks-content">
                      <label className="remarks-title" htmlFor="post-detail-remarks">Ask anything you want</label>
                      <textarea
                        id="post-detail-remarks"
                        className="remarks-textarea"
                        rows={3}
                        placeholder="Write something..."
                        value={remarks}
                        onChange={this.handleRemarksChange}
                      />
                    </div>
                  </div>

                  {hasDates && (
                    <div className="price-summary">
                      <p className="price-summary-calc">
                        BDT {formattedPrice} × {totalNights} {totalNights === 1 ? 'night' : 'nights'} × {rooms} {rooms === 1 ? 'room' : 'rooms'}
                      </p>
                      <div className="summary-divider" />
                      <div className="grand-total">
                        <span>Grand total</span>
                        <strong>BDT {formatMoney(total)}</strong>
                      </div>
                    </div>
                  )}

                  <button type="button" className="reserve-btn" onClick={this.handleBookingDirect}>
                    {isInstant ? 'Reserve Now' : 'Send Booking Request'}
                  </button>

                  <div className="booking-help">
                    Need help with booking? <a href="/contact">Ask Help &amp; Support</a>
                  </div>
                  <div className="report-listing">
                    <button type="button" className="report-link" onClick={() => this.openModal('report')}>
                      <i className="far fa-flag" /><span>Report this listing</span>
                    </button>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>

        <div className="mobile-sticky-bar">
          <div className="mobile-sticky-price-info">
            <span className="mobile-price-amount">BDT {formattedPrice}</span>
            <span className="mobile-price-unit"> / day</span>
          </div>
          <button type="button" className="mobile-sticky-reserve-btn" onClick={this.handleMobileAction}>
            {hasDates ? (isInstant ? 'Reserve' : 'Send Request') : 'Select Dates'}
          </button>
        </div>
      </div>
    );
  }
}

export default withNavigation(PostDetail);
